import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Sun, Wrench, FileText, Users, Shield, Bell, LogOut, User, ChevronDown, Menu, X, Sparkles, HeartPulse, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef, useEffect } from "react";

const NAV_ALL = [
    { section: "Fleet Operations", items: [
        { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["super_admin", "sales"] },
        { to: "/plants", label: "Fleet", icon: Sun, testid: "nav-fleet", roles: ["super_admin", "sales"] },
        { to: "/service-required", label: "Service Required", icon: Wrench, testid: "nav-service", roles: ["super_admin", "sales"] },
        { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts", roles: ["super_admin", "sales"] },
    ]},
    { section: "Business", items: [
        { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", roles: ["super_admin", "sales"] },
        { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports", roles: ["super_admin", "sales"] },
    ]},
    { section: "Administration", items: [
        { to: "/admin/plants/new", label: "Onboard Plant", icon: Sparkles, testid: "nav-onboard-plant", roles: ["super_admin", "sales"] },
        { to: "/admin/users", label: "Users", icon: Shield, testid: "nav-admin", roles: ["super_admin"] },
        { to: "/admin/system-health", label: "System Health", icon: HeartPulse, testid: "nav-system-health", roles: ["super_admin"] },
    ]},
];

const ROLE_META = {
    super_admin: { label: "Super Admin" },
    sales: { label: "Sales · Executive" },
    customer: { label: "Customer" },
};

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [sideOpen, setSideOpen] = useState(false);
    const menuRef = useRef();

    useEffect(() => {
        const h = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenuOpen(false);
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    useEffect(() => { setSideOpen(false); }, [location.pathname]);

    const toggleSide = () => setSideOpen(v => !v);

    const roleMeta = ROLE_META[user?.role] || ROLE_META.customer;
    const visibleSections = NAV_ALL.map(s => ({
        section: s.section,
        items: s.items.filter(i => i.roles.includes(user?.role))
    })).filter(s => s.items.length > 0);

    const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            {/* Light editorial header — white, subtle border, no dark chrome */}
            <header
                className="fixed top-0 left-0 right-0 bg-white"
                style={{
                    minHeight: "64px",
                    borderBottom: "1px solid #EAEAEA",
                    zIndex: 1100,
                    paddingTop: "env(safe-area-inset-top)",
                }}
                data-testid="app-header"
            >
                <div className="h-full flex items-center gap-2 sm:gap-4 px-3 sm:px-6">
                    <button
                        onClick={toggleSide}
                        className="w-11 h-11 rounded-full flex items-center justify-center text-zinc-900 hover:bg-zinc-100 transition-colors"
                        data-testid="sidebar-toggle"
                        aria-label={sideOpen ? "Hide menu" : "Show menu"}
                        aria-expanded={sideOpen}
                    >
                        {sideOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>

                    {/* Brand mark — RPPL logo, minimal white */}
                    <Link
                        to="/"
                        className="flex-shrink-0 flex items-center transition-opacity hover:opacity-80"
                        data-testid="brand-card"
                    >
                        <img src="/rppl-logo.png" alt="RPPL Solar" className="h-10 sm:h-14 w-auto object-contain" />
                    </Link>

                    <div className="flex-1" />

                    <SyncBadge />

                    {/* User menu — floating black avatar circle + name pill */}
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setMenuOpen(v => !v)}
                            className="flex items-center gap-2.5 pl-1 pr-3 h-11 rounded-full hover:bg-zinc-100 transition-colors"
                            data-testid="user-menu-trigger"
                        >
                            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#0A0A0A] text-white text-sm font-semibold">
                                {initial}
                            </div>
                            <div className="hidden sm:block text-left leading-tight">
                                <div className="text-xs text-zinc-900 font-semibold truncate max-w-[140px]">{user?.name}</div>
                                <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">{roleMeta.label}</div>
                            </div>
                            <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
                        </button>
                        {menuOpen && (
                            <div
                                className="absolute right-0 top-full mt-2 w-72 overflow-hidden bg-white"
                                style={{ border: "1px solid #EAEAEA", borderRadius: "16px", boxShadow: "0 12px 40px rgba(0,0,0,0.08)" }}
                                data-testid="user-menu"
                            >
                                <div className="p-4 border-b border-zinc-100">
                                    <div className="text-sm font-semibold text-zinc-900">{user?.name}</div>
                                    <div className="text-xs text-zinc-500 font-mono truncate mt-0.5">{user?.email}</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-900 text-white">
                                        {roleMeta.label}
                                    </div>
                                </div>
                                <NavLink to="/profile" onClick={() => setMenuOpen(false)} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 transition-colors border-b border-zinc-100" data-testid="menu-profile-link">
                                    <User className="w-4 h-4" /> My Profile
                                </NavLink>
                                <button onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 transition-colors" data-testid="logout-btn">
                                    <LogOut className="w-4 h-4" /> Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Backdrop scrim */}
            {sideOpen && (
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                    style={{ top: "calc(64px + env(safe-area-inset-top))", zIndex: 1000 }}
                    onClick={toggleSide}
                    data-testid="sidebar-scrim"
                />
            )}

            {/* Sidebar — dark editorial drawer with black active pill */}
            <aside
                className={`fixed left-0 bg-[#0A0A0A] overflow-y-auto transition-transform duration-200 ease-out ${sideOpen ? "translate-x-0" : "-translate-x-full"}`}
                style={{
                    top: "calc(64px + env(safe-area-inset-top))",
                    width: "260px",
                    height: "calc(100vh - 64px - env(safe-area-inset-top))",
                    borderRight: "1px solid rgba(255,255,255,0.05)",
                    zIndex: 1050,
                }}
                data-testid="app-sidebar"
                aria-hidden={!sideOpen}
            >
                <nav className="p-4 space-y-6">
                    {visibleSections.map((sec) => (
                        <div key={sec.section}>
                            <div className="text-[9px] font-semibold uppercase tracking-[0.22em] text-zinc-500 px-3 mb-2">
                                {sec.section}
                            </div>
                            <div className="space-y-0.5">
                                {sec.items.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        end={n.to === "/"}
                                        data-testid={n.testid}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2.5 rounded-full text-sm transition-all duration-200 ${
                                                isActive
                                                    ? "bg-white text-[#0A0A0A] font-semibold"
                                                    : "text-zinc-300 hover:bg-white/[0.08] hover:text-white"
                                            }`
                                        }
                                    >
                                        <n.icon className="w-4 h-4 flex-shrink-0" />
                                        {n.label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* RPPL brand mark at bottom */}
                    <div className="pt-6 mt-6 border-t border-white/10 flex items-center justify-center">
                        <div className="bg-white rounded-lg px-3 py-2">
                            <img src="/rppl-logo.png" alt="RPPL Solar" className="h-8 w-auto object-contain" />
                        </div>
                    </div>
                </nav>
            </aside>

            {/* Main content */}
            <main
                data-testid="main-content"
                className="pl-0"
                style={{ paddingTop: "calc(88px + env(safe-area-inset-top))" }}
            >
                <div className="px-3 sm:px-6 lg:px-8 pb-10 space-y-6 mx-auto" style={{ maxWidth: "1600px" }}>
                    <Outlet />
                </div>
            </main>
        </div>
    );
}

/**
 * SyncBadge — mini live indicator + manual "sync now" trigger.
 *
 * Shows when SolarOS was last polled and lets an operator force a fresh
 * poll when the console feels stale (editorial monochrome pill).
 */
function SyncBadge() {
    const [meta, setMeta] = useState(null);
    const [triggering, setTriggering] = useState(false);
    const [justSynced, setJustSynced] = useState(false);
    const load = () => import("@/lib/api").then(({ api }) =>
        api.get("/sync/status").then(r => setMeta(r.data)).catch(() => {})
    );
    useEffect(() => {
        let alive = true;
        const wrapped = async () => { if (alive) await load(); };
        wrapped();
        const t = setInterval(wrapped, 30000);
        return () => { alive = false; clearInterval(t); };
    }, []);
    const trigger = async () => {
        if (triggering) return;
        setTriggering(true);
        try {
            const { api } = await import("@/lib/api");
            const { data } = await api.post("/sync/trigger");
            if (data?.meta) {
                setMeta((prev) => ({ ...(prev || {}), ...data.meta }));
            } else {
                await load();
            }
            setJustSynced(true);
            setTimeout(() => setJustSynced(false), 2500);
        } catch (e) {
            // keep silent — operator can retry
        } finally {
            setTriggering(false);
        }
    };
    if (!meta) {
        return (
            <div className="hidden md:flex items-center gap-2 px-3 h-9 rounded-full text-[11px] font-mono bg-zinc-100 text-zinc-500"
                data-testid="header-live">
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                Connecting…
            </div>
        );
    }
    const inBackoff = meta.in_backoff;
    const lastSyncedAt = meta.last_synced_at ? new Date(meta.last_synced_at) : null;
    const ageSec = lastSyncedAt ? Math.floor((Date.now() - lastSyncedAt.getTime()) / 1000) : null;
    const stale = ageSec != null && ageSec > 180;
    const isWarn = inBackoff || stale;
    let label = "LIVE · SolarOS";
    if (inBackoff) label = `RATE LIMIT · ${Math.round(meta.backoff_seconds || 0)}s`;
    else if (justSynced) label = "SYNCED JUST NOW";
    else if (ageSec != null) label = ageSec < 90 ? "LIVE · SolarOS" : `SYNCED ${Math.round(ageSec / 60)}m ago`;

    return (
        <div className="hidden md:inline-flex items-stretch rounded-full overflow-hidden border border-zinc-200" data-testid="header-sync">
            <div className={`flex items-center gap-2 pl-3 pr-2.5 text-[11px] font-mono ${isWarn ? "bg-amber-50 text-amber-800" : "bg-zinc-50 text-zinc-700"}`}
                title={`Last synced ${lastSyncedAt ? lastSyncedAt.toLocaleTimeString() : "—"} · ${meta.stations} stations · ${meta.active_alerts} active`}
                data-testid="header-live">
                <span className={`w-1.5 h-1.5 rounded-full ${isWarn ? "bg-amber-500" : "bg-[#16A34A] animate-pulse"}`} />
                {label}
            </div>
            <button
                onClick={trigger}
                disabled={triggering || inBackoff}
                className={`flex items-center justify-center px-3 border-l border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                title={inBackoff ? "SolarOS is rate-limiting — can't force sync right now" : "Force a fresh SolarOS poll now"}
                data-testid="sync-now-btn"
                aria-label="Sync now"
            >
                <RefreshCw className={`w-3.5 h-3.5 text-zinc-700 ${triggering ? "animate-spin" : ""}`} />
            </button>
        </div>
    );
}
